import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Custom hook for managing timeline and element dragging
 * @param {React.RefObject} videoRef - Reference to the video element
 * @param {React.RefObject} mainTimelineRef - Reference to the main timeline element
 * @param {React.RefObject} highlightsTimelineRef - Reference to highlights timeline
 * @param {React.RefObject} videoContainerRef - Reference to video container
 * @param {React.RefObject} zoomRectRef - Reference to zoom rectangle
 * @param {number} duration - Video duration
 * @param {Object} selectedHighlight - Currently selected highlight
 * @param {Function} onUpdateHighlight - Callback to update highlight
 * @param {Function} onUpdateTextOverlay - Callback to update text overlay
 * @param {Function} onMouseDown - Callback for mouse down on highlight
 * @param {Function} setCurrentTime - Callback to set current time
 * @returns {Object} Drag state and handler functions
 */
const useTimelineDrag = ({
  videoRef,
  mainTimelineRef,
  highlightsTimelineRef,
  videoContainerRef,
  zoomRectRef,
  duration,
  selectedHighlight,
  onUpdateHighlight,
  onUpdateTextOverlay,
  onMouseDown,
  setCurrentTime,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isTimelineDragging, setIsTimelineDragging] = useState(false);
  const [isZoomRectDragging, setIsZoomRectDragging] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);

  // Track whether playback should resume after dragging
  const shouldResumePlayRef = useRef(false);

  // Timeline marker drag handlers
  const handleTimelineDragStart = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();

    if (videoRef.current && !videoRef.current.paused) {
      shouldResumePlayRef.current = true;
      videoRef.current.pause();
    } else {
      shouldResumePlayRef.current = false;
    }
    setIsTimelineDragging(true);
  }, [videoRef]);

  const handleTimelineDragMove = useCallback((clientX) => {
    if (isTimelineDragging && mainTimelineRef.current && videoRef.current) {
      const rect = mainTimelineRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
      const percentage = x / rect.width;
      const newTime = percentage * duration;
      setCurrentTime(newTime);
      videoRef.current.currentTime = newTime;
    }
  }, [isTimelineDragging, mainTimelineRef, videoRef, duration, setCurrentTime]);

  const handleTimelineDragEnd = useCallback(() => {
    if (isTimelineDragging) {
      if (shouldResumePlayRef.current && videoRef.current) {
        videoRef.current.play();
      }
      setIsTimelineDragging(false);
      shouldResumePlayRef.current = false;
    }
  }, [isTimelineDragging, videoRef]);

  // Highlight drag handlers
  const handleHighlightDragStart = useCallback((e, highlight) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDragging(true);
    setDraggedItem({ type: 'highlight', item: highlight });
    if (onMouseDown) {
      onMouseDown(highlight, e);
    }
  }, [onMouseDown]);

  const handleDragMove = useCallback((clientX) => {
    if (!isDragging || !draggedItem || !highlightsTimelineRef.current) return;

    const rect = highlightsTimelineRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const newStartTime = (x / rect.width) * duration;

    if (draggedItem.type === 'highlight' && draggedItem.item) {
      const maxStart = duration - draggedItem.item.duration;
      const boundedStartTime = Math.max(0, Math.min(newStartTime, maxStart));
      if (onUpdateHighlight) {
        onUpdateHighlight('startTime', boundedStartTime);
      }
    } else if (draggedItem.type === 'text' && draggedItem.item) {
      const maxStart = duration - draggedItem.item.duration;
      const boundedStartTime = Math.max(0, Math.min(newStartTime, maxStart));
      if (onUpdateTextOverlay) {
        onUpdateTextOverlay('startTime', boundedStartTime);
      }
    }
  }, [isDragging, draggedItem, highlightsTimelineRef, duration, onUpdateHighlight, onUpdateTextOverlay]);

  // Zoom Rectangle drag handlers
  const handleZoomRectDragStart = useCallback((e) => {
    if (!zoomRectRef.current || !videoContainerRef.current || !selectedHighlight?.zoom?.enabled) return;

    e.preventDefault();
    e.stopPropagation();

    setIsZoomRectDragging(true);

    const rect = videoContainerRef.current.getBoundingClientRect();
    const clientX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
    const clientY = e.type.includes('mouse') ? e.clientY : e.touches[0].clientY;

    zoomRectRef.current.dataset.startX = clientX;
    zoomRectRef.current.dataset.startY = clientY;
    zoomRectRef.current.dataset.initialLeft = parseInt(zoomRectRef.current.style.left) || 0;
    zoomRectRef.current.dataset.initialTop = parseInt(zoomRectRef.current.style.top) || 0;
    zoomRectRef.current.dataset.containerWidth = rect.width;
    zoomRectRef.current.dataset.containerHeight = rect.height;
  }, [zoomRectRef, videoContainerRef, selectedHighlight]);

  const handleZoomRectDragMove = useCallback((clientX, clientY) => {
    if (!isZoomRectDragging || !zoomRectRef.current || !videoContainerRef.current) return;

    const startX = parseInt(zoomRectRef.current.dataset.startX);
    const startY = parseInt(zoomRectRef.current.dataset.startY);
    const initialLeft = parseInt(zoomRectRef.current.dataset.initialLeft);
    const initialTop = parseInt(zoomRectRef.current.dataset.initialTop);
    const containerWidth = parseInt(zoomRectRef.current.dataset.containerWidth);
    const containerHeight = parseInt(zoomRectRef.current.dataset.containerHeight);

    const rectWidth = containerWidth / 2;
    const rectHeight = containerHeight / 2;

    let newLeft = initialLeft + (clientX - startX);
    let newTop = initialTop + (clientY - startY);

    newLeft = Math.max(0, Math.min(newLeft, containerWidth - rectWidth));
    newTop = Math.max(0, Math.min(newTop, containerHeight - rectHeight));

    zoomRectRef.current.style.left = `${newLeft}px`;
    zoomRectRef.current.style.top = `${newTop}px`;

    const xPercent = Math.round(((newLeft / (containerWidth - rectWidth)) * 2 - 1) * 100);
    const yPercent = Math.round(((newTop / (containerHeight - rectHeight)) * 2 - 1) * 100);

    if (selectedHighlight && selectedHighlight.zoom && onUpdateHighlight) {
      onUpdateHighlight('zoom', {
        ...selectedHighlight.zoom,
        x: xPercent,
        y: yPercent
      });
    }
  }, [isZoomRectDragging, zoomRectRef, videoContainerRef, selectedHighlight, onUpdateHighlight]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setDraggedItem(null);
    setIsZoomRectDragging(false);
  }, []);

  // Text overlay drag start
  const handleTextDragStart = useCallback((e, textOverlay, onSelectTextOverlay) => {
    e.stopPropagation();
    setIsDragging(true);
    setDraggedItem({ type: 'text', item: textOverlay });
    if (onSelectTextOverlay) {
      onSelectTextOverlay(textOverlay);
    }
  }, []);

  // Global mouse/touch event handlers
  const handleMouseMove = useCallback((e) => {
    if (isTimelineDragging) {
      handleTimelineDragMove(e.clientX);
    } else if (isDragging) {
      handleDragMove(e.clientX);
    } else if (isZoomRectDragging) {
      handleZoomRectDragMove(e.clientX, e.clientY);
    }
  }, [isTimelineDragging, isDragging, isZoomRectDragging, handleTimelineDragMove, handleDragMove, handleZoomRectDragMove]);

  const handleTouchMove = useCallback((e) => {
    if (isTimelineDragging || isDragging || isZoomRectDragging) {
      e.preventDefault();
      if (e.touches && e.touches[0]) {
        const clientX = e.touches[0].clientX;
        const clientY = e.touches[0].clientY;

        if (isTimelineDragging) {
          handleTimelineDragMove(clientX);
        } else if (isDragging) {
          handleDragMove(clientX);
        } else if (isZoomRectDragging) {
          handleZoomRectDragMove(clientX, clientY);
        }
      }
    }
  }, [isTimelineDragging, isDragging, isZoomRectDragging, handleTimelineDragMove, handleDragMove, handleZoomRectDragMove]);

  // Set up global event listeners
  useEffect(() => {
    const handleGlobalMouseMove = (e) => handleMouseMove(e);
    const handleGlobalMouseUp = () => {
      handleTimelineDragEnd();
      handleDragEnd();
    };
    const handleGlobalTouchMove = (e) => handleTouchMove(e);
    const handleGlobalTouchEnd = handleGlobalMouseUp;

    if (isTimelineDragging || isDragging || isZoomRectDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
      document.addEventListener('touchend', handleGlobalTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
        document.removeEventListener('touchmove', handleGlobalTouchMove);
        document.removeEventListener('touchend', handleGlobalTouchEnd);
      };
    }
  }, [isTimelineDragging, isDragging, isZoomRectDragging, handleMouseMove, handleTouchMove, handleTimelineDragEnd, handleDragEnd]);

  return {
    isDragging,
    setIsDragging,
    isTimelineDragging,
    isZoomRectDragging,
    draggedItem,
    setDraggedItem,
    handleTimelineDragStart,
    handleTimelineDragMove,
    handleTimelineDragEnd,
    handleHighlightDragStart,
    handleDragMove,
    handleZoomRectDragStart,
    handleZoomRectDragMove,
    handleDragEnd,
    handleTextDragStart,
  };
};

export default useTimelineDrag;
